import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Download, ExternalLink, Code, Palette, Zap, Linkedin, Target, Users, TrendingUp, Clock, MapPin } from 'lucide-react';

const Home = () => {


  const leadershipPrinciples = [
    {
      icon: Clock,
      title: 'Long-Term Strategic Thinking',
      description: 'Aligns today\'s initiatives with decade-scale objectives, turning near-term wins into enduring competitive advantage.',
      color: 'from-blue-100 to-blue-200',
      iconColor: 'text-blue-700'
    },
    {
      icon: Target,
      title: 'Strategic Clarity',
      description: 'Transform complex challenges into clear, executable strategies that align stakeholders and drive measurable outcomes across time horizons.',
      color: 'from-primary-100 to-accent-100',
      iconColor: 'text-primary-700'
    },
    {
      icon: Users,
      title: 'Cross-Functional Leadership',
      description: 'Bridge silos and build consensus across diverse teams to achieve organizational objectives through collaborative execution.',
      color: 'from-green-100 to-green-200',
      iconColor: 'text-green-700'
    },
    {
      icon: Zap,
      title: 'AI-Enabled Transformation',
      description: 'Leverage emerging technologies to create sustainable competitive advantages and operational excellence for the future.',
      color: 'from-yellow-100 to-yellow-200',
      iconColor: 'text-yellow-700'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col justify-between">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-accent-50 pt-20">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-900 leading-tight">
              Strategic Leadership for the Long Now
            </h1>
            {/* Remove the redundant role line below */}
            {/* Add the callout card below */}
            <p className="text-lg text-primary-800 max-w-3xl mx-auto leading-relaxed">
              I partner with executives to turn complex business challenges into strategic opportunities. With 10+ years leading AI strategy, product innovation, and business development, I help companies make decisions today that compound long-term value.
            </p>
            <div className="w-full flex flex-col items-center justify-center gap-4">
              <div className="bg-primary-50 border border-primary-100 rounded-2xl px-8 py-6 w-full max-w-3xl flex items-center justify-center">
                <span className="text-2xl font-semibold text-primary-700 text-center">
                  I believe in influencing individuals & organizations to make the short-term decisions that benefit the long now.
                </span>
              </div>
            </div>
            <p className="text-lg text-primary-800 max-w-3xl mx-auto leading-relaxed">
              My expertise is in aligning cross-functional teams and delivering measurable outcomes through strategic clarity & operational excellence.
            </p>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.2 }}
              className="flex justify-center mt-8 mb-4"
            >
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-6 h-10 border-2 border-primary-400 rounded-full flex justify-center"
              >
                <motion.div
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-1 h-3 bg-primary-400 rounded-full mt-2"
                />
              </motion.div>
            </motion.div>

            {/* Executive Value Proposition */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-primary-100 mt-8">
              <p className="text-primary-800 font-medium text-lg">
                <span className="text-primary-900 font-semibold">Current Focus:</span> Chief of Staff · Business Development · Corporate & Product Strategy
              </p>
              <p className="text-primary-700 mt-2">
                I bridge vision and execution, driving growth through strategic partnerships, AI innovation, and scalable business models.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <a
                href="/contact"
                className="btn-primary"
              >
                Start a Conversation
              </a>
              <a
                href="/projects"
                className="btn-secondary"
              >
                View Projects
              </a>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        {/* Removed from absolute bottom position */}
      </section>

      {/* Core Leadership Principles (moved from About) */}
      <section className="section bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="section-title">Core Leadership Principles</h2>
            <p className="section-subtitle">
              The foundational principles that guide my approach to strategic leadership
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {leadershipPrinciples.map((principle, index) => (
              <motion.div
                key={principle.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card"
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${principle.color} rounded-lg flex items-center justify-center mb-4`}>
                  <principle.icon size={24} className={principle.iconColor} />
                </div>
                <h3 className="text-lg font-semibold text-primary-900 mb-3">
                  {principle.title}
                </h3>
                <p className="text-primary-800">
                  {principle.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      {/* Subfooter Section */}
      <section className="section bg-primary-50 border-t border-primary-100">
        <div className="max-w-2xl mx-auto flex flex-col items-center gap-4 text-center">
          <a
            href="https://www.linkedin.com/in/neilsinghmamak/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary-700 hover:text-primary-900 font-medium text-lg"
          >
            <Linkedin size={24} />
            Connect on LinkedIn
          </a>
          <span className="text-primary-800 text-sm">
            or email me at: <a href="mailto:hello@hyperhumane.com" className="underline hover:text-primary-700">hello@hyperhumane.com</a>
          </span>
        </div>
      </section>
    </div>
  );
};

export default Home; 